import React, { useState, useEffect, useRef } from 'react';
import confetti from 'canvas-confetti';
import {
  DEFAULT_STOP_CATEGORIES,
  StopCategory,
  validateWordWithApi,
  calculateStopScores,
  WordValidationResult,
  getCategorySuggestions
} from '../../services/stopGameValidator';
import { getRandomUnusedLetter } from '../../services/stopGameRoomService';
import {
  playBuzzerSound,
  playSuccessSound,
  playTickSound,
  speakWord,
  speakLetter
} from '../../utils/stopGameAudio';
import StopLetterWheel from './StopLetterWheel';
import {
  Home,
  Users,
  Timer,
  Volume2,
  CheckCircle2,
  XCircle,
  Trophy,
  RotateCcw,
  Sparkles,
  Search,
  BookOpen,
  ArrowLeft,
  Plus,
  Trash2,
  Award,
  ThumbsUp,
  ThumbsDown,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

interface StopGamePartyProps {
  onBackToMenu: () => void;
}

export const StopGameParty: React.FC<StopGamePartyProps> = ({ onBackToMenu }) => {
  // Room Players
  const [playerNames, setPlayerNames] = useState<string[]>(['Player 1', 'Player 2']);
  const [newPlayerInput, setNewPlayerInput] = useState('');
  const [playerScores, setPlayerScores] = useState<Record<string, number>>({});

  // Board settings
  const [timerDuration, setTimerDuration] = useState(60); // 0 = untimed
  const [selectedCategories, setSelectedCategories] = useState<StopCategory[]>(
    DEFAULT_STOP_CATEGORIES.slice(0, 6)
  );

  // Round State
  const [currentRound, setCurrentRound] = useState(1);
  const [currentLetter, setCurrentLetter] = useState('A');
  const [usedLetters, setUsedLetters] = useState<string[]>([]);
  const [isSpinning, setIsSpinning] = useState(false);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [timeLeft, setTimeLeft] = useState(60);
  const [hasStopped, setHasStopped] = useState(false);

  // Score Calculator & Referee State
  const [roundPlayerAnswers, setRoundPlayerAnswers] = useState<Record<string, Record<string, string>>>({});
  const [validatingWords, setValidatingWords] = useState(false);
  const [roundValidations, setRoundValidations] = useState<Record<string, Record<string, WordValidationResult>>>({});
  const [calculatedRoundScores, setCalculatedRoundScores] = useState<Record<string, { roundScore: number; categoryScores: Record<string, number> }> | null>(null);
  const [wordOverrides, setWordOverrides] = useState<Record<string, Record<string, 'accepted' | 'rejected' | null>>>({});
  const [partyReviewMode, setPartyReviewMode] = useState<'table' | 'player'>('table');
  const [activePartyPlayerIndex, setActivePartyPlayerIndex] = useState(0);

  // Quick Referee Word Lookup (any player in the room can test a word)
  const [refereeWord, setRefereeWord] = useState('');
  const [refereeCategory, setRefereeCategory] = useState(DEFAULT_STOP_CATEGORIES[0].id);
  const [refereeResult, setRefereeResult] = useState<WordValidationResult | null>(null);
  const [refereeChecking, setRefereeChecking] = useState(false);

  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Timer countdown
  useEffect(() => {
    if (!isTimerRunning || timerDuration === 0) return;

    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
          setTimeout(() => {
            handleBuzzerStop();
          }, 0);
          return 0;
        }
        if (prev <= 5) {
          playTickSound();
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isTimerRunning, timerDuration]);

  const handleAddPlayer = () => {
    const trimmed = newPlayerInput.trim();
    if (!trimmed) return;
    if (playerNames.includes(trimmed)) {
      alert('A player with this name is already in the room.');
      return;
    }
    setPlayerNames(prev => [...prev, trimmed]);
    setPlayerScores(prev => ({ ...prev, [trimmed]: 0 }));
    setNewPlayerInput('');
  };

  const handleRemovePlayer = (name: string) => {
    if (playerNames.length <= 1) {
      alert('Need at least 1 player');
      return;
    }
    setPlayerNames(prev => prev.filter(p => p !== name));
    setPlayerScores(prev => {
      const copy = { ...prev };
      delete copy[name];
      return copy;
    });
  };

  const startNewRound = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    const letter = getRandomUnusedLetter(usedLetters);
    const updatedUsed = [...usedLetters, letter];

    setCurrentLetter(letter);
    setUsedLetters(updatedUsed);
    setIsSpinning(true);
    setHasStopped(false);
    setTimeLeft(timerDuration);
    setIsTimerRunning(false);
    setCalculatedRoundScores(null);
    setRoundValidations({});
    setWordOverrides({});

    // Reset round answers
    const initialAnswers: Record<string, Record<string, string>> = {};
    for (const p of playerNames) {
      initialAnswers[p] = {};
    }
    setRoundPlayerAnswers(initialAnswers);
  };

  const handleStartTimer = () => {
    setIsTimerRunning(true);
    setHasStopped(false);
  };

  const handleBuzzerStop = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    setIsTimerRunning(false);
    setHasStopped(true);
    playBuzzerSound();
  };

  // Referee Quick Search
  const handleCheckRefereeWord = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!refereeWord.trim()) return;

    setRefereeChecking(true);
    setRefereeResult(null);

    const res = await validateWordWithApi(refereeWord.trim(), currentLetter, refereeCategory);
    setRefereeResult(res);
    setRefereeChecking(false);

    if (res.isValid) {
      playSuccessSound();
    }
  };

  // Batch Validate All Players for the Round
  const handleValidateAllRound = async () => {
    setValidatingWords(true);
    const validations: Record<string, Record<string, WordValidationResult>> = {};

    for (const player of playerNames) {
      validations[player] = {};
      const answers = roundPlayerAnswers[player] || {};

      for (const cat of selectedCategories) {
        const word = answers[cat.id] || '';
        const res = await validateWordWithApi(word, currentLetter, cat.id);
        validations[player][cat.id] = res;
      }
    }

    setRoundValidations(validations);

    // Compute Stop Scores (10 unique, 5 duplicate, 0 invalid)
    const scores = calculateStopScores(roundPlayerAnswers, validations);
    setCalculatedRoundScores(scores);

    // Add to total player scores
    setPlayerScores(prev => {
      const updated = { ...prev };
      for (const player of playerNames) {
        updated[player] = (updated[player] || 0) + (scores[player]?.roundScore || 0);
      }
      return updated;
    });

    playSuccessSound();
    setPartyReviewMode('player');
    setActivePartyPlayerIndex(0);
    setValidatingWords(false);
  };

  const handleVoteToggle = (player: string, catId: string, vote: 'accepted' | 'rejected' | null) => {
    const updatedOverrides = {
      ...wordOverrides,
      [player]: {
        ...(wordOverrides[player] || {}),
        [catId]: vote
      }
    };
    setWordOverrides(updatedOverrides);

    const votes: Record<string, Record<string, Record<string, boolean>>> = {};
    for (const p of playerNames) {
      votes[p] = {};
      for (const cat of selectedCategories) {
        const status = updatedOverrides[p]?.[cat.id];
        if (status === 'accepted') {
          votes[p][cat.id] = { referee: true };
        } else if (status === 'rejected') {
          votes[p][cat.id] = { referee: false };
        }
      }
    }

    const newScores = calculateStopScores(roundPlayerAnswers, roundValidations, votes);
    setCalculatedRoundScores(newScores);

    // Update total scores by the difference
    setPlayerScores(prev => {
      const copy = { ...prev };
      for (const p of playerNames) {
        const oldRound = calculatedRoundScores?.[p]?.roundScore || 0;
        const newRound = newScores[p]?.roundScore || 0;
        copy[p] = Math.max(0, (copy[p] || 0) + (newRound - oldRound));
      }
      return copy;
    });
  };

  const sortedLeaderboard = [...playerNames].sort(
    (a, b) => (playerScores[b] || 0) - (playerScores[a] || 0)
  );

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      {/* Top Bar */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={onBackToMenu}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors text-sm font-semibold"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Modes
        </button>

        <div className="flex items-center gap-2">
          <span className="text-xs font-black uppercase text-emerald-800 bg-emerald-100 px-3 py-1 rounded-full flex items-center gap-1">
            <Home className="w-3.5 h-3.5" />
            In-Person Party & Classroom Mode
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* LEFT / CENTER: The Tabletop Game Board */}
        <div className="lg:col-span-2 space-y-6">
          {/* Letter & Timer Central Display */}
          <div className="bg-white rounded-3xl p-6 md:p-8 shadow-xl border border-gray-100 text-center relative overflow-hidden">
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold text-gray-500 uppercase">
                Round {currentRound}
              </span>
              <button
                onClick={startNewRound}
                className="px-3 py-1.5 text-xs font-bold bg-purple-100 hover:bg-purple-200 text-purple-800 rounded-xl transition-all"
              >
                Spin New Letter 🎲
              </button>
            </div>

            <StopLetterWheel
              letter={currentLetter}
              isSpinning={isSpinning}
              onSpinComplete={() => setIsSpinning(false)}
              size="lg"
            />

            {/* Big Timer and STOP Buzzer */}
            <div className="mt-4 max-w-sm mx-auto">
              {timerDuration > 0 && (
                <div className="mb-4">
                  <div className="text-5xl font-mono font-black text-gray-900 tracking-tight">
                    {timeLeft}s
                  </div>
                  <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden mt-2">
                    <div
                      className={`h-full transition-all duration-1000 ${
                        timeLeft <= 10 ? 'bg-red-500' : 'bg-gradient-to-r from-emerald-500 to-indigo-500'
                      }`}
                      style={{ width: `${(timeLeft / timerDuration) * 100}%` }}
                    />
                  </div>
                </div>
              )}

              <div className="flex justify-center gap-3">
                {!isTimerRunning && !hasStopped && (
                  <button
                    onClick={handleStartTimer}
                    disabled={isSpinning}
                    className={`px-8 py-3.5 bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold text-base rounded-2xl shadow-lg transition-all transform hover:scale-105 ${
                      isSpinning ? 'opacity-50 cursor-not-allowed transform-none' : ''
                    }`}
                  >
                    {isSpinning ? 'Choosing Letter... 🎲' : 'Start Round Timer ⏱️'}
                  </button>
                )}

                {(isTimerRunning || (!hasStopped && !isSpinning)) && (
                  <button
                    onClick={handleBuzzerStop}
                    disabled={isSpinning}
                    className="px-10 py-3.5 bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 text-white font-black text-xl uppercase tracking-wider rounded-2xl shadow-xl shadow-red-500/30 transition-all transform hover:scale-105 active:scale-95 disabled:opacity-50"
                  >
                    🛑 STOP!
                  </button>
                )}

                {hasStopped && (
                  <button
                    onClick={() => {
                      setCurrentRound(r => r + 1);
                      startNewRound();
                    }}
                    className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-2xl shadow-lg transition-all"
                  >
                    Next Round ➡️
                  </button>
                )}
              </div>

              {hasStopped && (
                <div className="mt-3 text-red-600 font-black text-sm uppercase tracking-wide animate-bounce">
                  🛑 Pencils Down! Round Stopped!
                </div>
              )}
            </div>
          </div>

          {/* Categories Board for the Room */}
          <div className="bg-white rounded-3xl p-6 shadow-md border border-gray-100">
            <h3 className="text-base font-extrabold text-gray-900 mb-4 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-600" />
              Round Categories (All words must start with &ldquo;{currentLetter}&rdquo;)
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {selectedCategories.map(cat => (
                <div
                  key={cat.id}
                  className="p-3 bg-purple-50/50 rounded-2xl border border-purple-100 flex items-center space-x-3"
                >
                  <span className="text-2xl">{cat.icon}</span>
                  <div>
                    <div className="font-extrabold text-sm text-gray-900">{cat.name}</div>
                    <div className="text-[10px] text-gray-500">{cat.description}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Referee / Word Checker */}
          <div className="bg-white rounded-3xl p-6 shadow-md border border-gray-100">
            <h3 className="text-base font-extrabold text-gray-900 mb-2 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-indigo-600" />
              English Dictionary Referee & Dispute Resolver
            </h3>
            <p className="text-xs text-gray-500 mb-4">
              Is someone in the room arguing whether their word is a valid English word? Type it here to check with the API!
            </p>

            <form onSubmit={handleCheckRefereeWord} className="flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                value={refereeWord}
                onChange={e => setRefereeWord(e.target.value)}
                placeholder={`Check word starting with "${currentLetter}"...`}
                className="flex-1 px-4 py-2.5 rounded-xl border border-gray-300 focus:ring-2 focus:ring-indigo-400 focus:outline-none text-sm font-semibold"
              />
              <select
                value={refereeCategory}
                onChange={e => setRefereeCategory(e.target.value)}
                className="px-3 py-2.5 rounded-xl border border-gray-300 bg-white text-xs font-bold"
              >
                {selectedCategories.map(cat => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
              <button
                type="submit"
                disabled={refereeChecking || !refereeWord.trim()}
                className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-sm rounded-xl transition-all disabled:opacity-50 flex items-center justify-center gap-1.5"
              >
                <Search className="w-4 h-4" />
                {refereeChecking ? 'Checking...' : 'Check Word'}
              </button>
            </form>

            {/* Referee Lookup Result Card */}
            {refereeResult && (
              <div
                className={`mt-4 p-4 rounded-2xl border transition-all ${
                  refereeResult.isValid
                    ? 'bg-emerald-50 border-emerald-200'
                    : 'bg-red-50 border-red-200'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {refereeResult.isValid ? (
                      <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-600" />
                    )}
                    <span className="font-extrabold text-base text-gray-900 uppercase">
                      {refereeResult.word || refereeWord}
                    </span>
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full font-bold ${
                        refereeResult.isValid
                          ? 'bg-emerald-200 text-emerald-900'
                          : 'bg-red-200 text-red-900'
                      }`}
                    >
                      {refereeResult.isValid ? 'VALID ENGLISH WORD' : 'INVALID'}
                    </span>
                  </div>

                  {refereeResult.isValid && refereeResult.word && (
                    <button
                      onClick={() => speakWord(refereeResult.word!)}
                      className="p-1.5 text-indigo-600 hover:bg-white rounded-lg transition-colors border border-indigo-100"
                      title="Hear pronunciation"
                    >
                      <Volume2 className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {refereeResult.phonetic && (
                  <div className="text-xs font-mono text-purple-700 mt-1">
                    {refereeResult.phonetic}
                  </div>
                )}

                {refereeResult.isTypo && refereeResult.correctedWord && (
                  <div className="mt-2 text-xs bg-amber-100 text-amber-900 p-2 rounded-xl flex items-center justify-between font-bold">
                    <span>⚠️ Spelling mistake (-8 pts): Did you mean &ldquo;{refereeResult.correctedWord}&rdquo;?</span>
                    <button
                      type="button"
                      onClick={() => speakWord(refereeResult.correctedWord!)}
                      className="p-1 text-amber-800 hover:bg-amber-200 rounded-lg ml-2"
                      title="Hear correct pronunciation"
                    >
                      <Volume2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}

                {refereeResult.definition && (
                  <p className="text-xs text-gray-700 mt-2 bg-white/70 p-2 rounded-xl">
                    <strong>Definition:</strong> {refereeResult.definition}
                  </p>
                )}

                {!refereeResult.isValid && (
                  <p className="text-xs text-red-700 font-medium mt-1">
                    {refereeResult.reason || 'Not found in the dictionary.'}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Batch Answer Entry & Validation Matrix */}
          <div className="bg-white rounded-3xl p-6 shadow-md border border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-extrabold text-gray-900 flex items-center gap-2">
                <Award className="w-5 h-5 text-purple-600" />
                Scorekeeper / Pass &amp; Play Entry
              </h3>
              <button
                onClick={handleValidateAllRound}
                disabled={validatingWords}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-extrabold text-xs rounded-xl shadow-md transition-all disabled:opacity-50"
              >
                {validatingWords ? 'Validating via API...' : 'Validate All Answers ✨'}
              </button>
            </div>
            <p className="text-xs text-gray-500 mb-4">
              Enter what each player wrote on paper to automatically check all words and score the round.
            </p>

            {Object.keys(roundValidations).length > 0 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-2.5 p-3 mb-4 bg-purple-50/70 rounded-2xl border border-purple-200">
                <div className="flex items-center gap-1.5 w-full sm:w-auto">
                  <button
                    onClick={() => setPartyReviewMode('player')}
                    className={`flex-1 sm:flex-initial px-3 py-1.5 rounded-xl font-bold text-xs flex items-center justify-center gap-1.5 transition-all ${
                      partyReviewMode === 'player'
                        ? 'bg-purple-600 text-white shadow-xs'
                        : 'bg-white text-purple-900 hover:bg-purple-100 border border-purple-200'
                    }`}
                  >
                    <Users className="w-3.5 h-3.5" />
                    <span>Validate Player-by-Player</span>
                  </button>
                  <button
                    onClick={() => setPartyReviewMode('table')}
                    className={`flex-1 sm:flex-initial px-3 py-1.5 rounded-xl font-bold text-xs flex items-center justify-center gap-1.5 transition-all ${
                      partyReviewMode === 'table'
                        ? 'bg-purple-600 text-white shadow-xs'
                        : 'bg-white text-purple-900 hover:bg-purple-100 border border-purple-200'
                    }`}
                  >
                    <span>Table Matrix</span>
                  </button>
                </div>

                {partyReviewMode === 'player' && (
                  <div className="flex items-center gap-1 overflow-x-auto w-full sm:w-auto pb-1 sm:pb-0">
                    {playerNames.map((p, idx) => (
                      <button
                        key={p}
                        onClick={() => setActivePartyPlayerIndex(idx)}
                        className={`px-3 py-1 rounded-xl text-xs font-bold transition-all whitespace-nowrap flex items-center gap-1 ${
                          activePartyPlayerIndex === idx
                            ? 'bg-indigo-600 text-white shadow-xs'
                            : 'bg-white text-gray-700 hover:bg-indigo-50 border border-gray-200'
                        }`}
                      >
                        <span>{p}</span>
                        <span className="text-[10px] opacity-80">
                          (+{calculatedRoundScores?.[p]?.roundScore || 0})
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {partyReviewMode === 'player' && Object.keys(roundValidations).length > 0 ? (() => {
              const safeIdx = Math.min(Math.max(0, activePartyPlayerIndex), playerNames.length - 1);
              const activePlayer = playerNames[safeIdx] || playerNames[0];
              const pRoundScore = calculatedRoundScores?.[activePlayer]?.roundScore || 0;

              return (
                <div className="space-y-4 animate-in fade-in">
                  <div className="p-3.5 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-2xl border border-purple-200 flex items-center justify-between">
                    <div>
                      <h4 className="font-extrabold text-gray-900 text-base">
                        Validating {activePlayer}&apos;s Words
                      </h4>
                      <p className="text-xs text-gray-500">
                        Room check: The room can review and dispute any word.
                      </p>
                    </div>
                    <div className="text-right">
                      <span className="text-xs font-bold text-gray-500">Score</span>
                      <div className="text-xl font-black text-purple-800">+{pRoundScore} pts</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {selectedCategories.map(cat => {
                      const userWord = roundPlayerAnswers[activePlayer]?.[cat.id] || '';
                      const valResult = roundValidations[activePlayer]?.[cat.id];
                      const catScore = calculatedRoundScores?.[activePlayer]?.categoryScores?.[cat.id] ?? (valResult?.isValid ? 10 : 0);
                      const currentOverride = wordOverrides[activePlayer]?.[cat.id];

                      return (
                        <div
                          key={cat.id}
                          className={`p-3.5 rounded-2xl border transition-all ${
                            currentOverride === 'rejected'
                              ? 'bg-red-50/70 border-red-300'
                              : catScore > 0
                                ? 'bg-emerald-50/60 border-emerald-300'
                                : 'bg-red-50/40 border-red-200'
                          }`}
                        >
                          <div className="flex items-center justify-between text-xs font-bold">
                            <span className="flex items-center gap-1.5 text-gray-900">
                              <span>{cat.icon}</span>
                              <span>{cat.name}</span>
                            </span>
                            <span className={`px-2 py-0.5 rounded-full font-bold text-xs ${
                              catScore > 0 ? 'bg-emerald-200 text-emerald-800' : 'bg-red-200 text-red-800'
                            }`}>
                              +{catScore} pts
                            </span>
                          </div>

                          <div className="flex items-center justify-between mt-2">
                            <span className="font-extrabold text-sm text-gray-900">
                              {userWord || <span className="text-gray-400 italic font-normal">None</span>}
                            </span>
                            {userWord && valResult?.isValid && (
                              <button
                                onClick={() => speakWord(valResult.correctedWord || userWord)}
                                className="p-1 text-purple-700 hover:text-purple-900 bg-white rounded border border-purple-100"
                                title="Hear pronunciation"
                              >
                                <Volume2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>

                          {valResult?.isTypo && valResult.correctedWord && currentOverride !== 'rejected' && (
                            <div className="text-xs bg-amber-100 text-amber-900 font-bold p-1.5 rounded-lg mt-1.5 flex items-center justify-between">
                              <span>⚠️ Typo (-8 pts): Did you mean &ldquo;{valResult.correctedWord}&rdquo;?</span>
                              <button
                                onClick={() => speakWord(valResult.correctedWord!)}
                                className="p-0.5 text-amber-900 hover:bg-amber-200 rounded"
                              >
                                <Volume2 className="w-3 h-3" />
                              </button>
                            </div>
                          )}

                          {valResult?.definition && (
                            <div className="text-[11px] text-gray-600 mt-1.5 bg-white/70 p-2 rounded-lg border border-gray-100">
                              {valResult.partOfSpeech && (
                                <span className="italic font-semibold text-purple-800 mr-1">
                                  [{valResult.partOfSpeech}]
                                </span>
                              )}
                              <span>{valResult.definition}</span>
                            </div>
                          )}

                          {!valResult?.isValid && userWord && currentOverride !== 'accepted' && (
                            <div className="text-[11px] text-red-700 font-medium mt-1">
                              {valResult?.reason || 'Invalid word'}
                            </div>
                          )}

                          {/* Word Suggestions for wrong or empty words */}
                          {(!valResult || !valResult.isValid) && (
                            <div className="text-xs bg-purple-50/80 border border-purple-200 p-2 rounded-xl mt-2">
                              <div className="font-bold text-purple-900 mb-1 flex items-center gap-1">
                                <Sparkles className="w-3 h-3 text-purple-600" />
                                {userWord ? 'Valid examples you could have used:' : 'Words you could have used:'}
                              </div>
                              <div className="flex flex-wrap gap-1">
                                {((valResult?.suggestedExamples && valResult.suggestedExamples.length > 0)
                                  ? valResult.suggestedExamples
                                  : getCategorySuggestions(currentLetter, cat.id)
                                ).slice(0, 4).map(ex => (
                                  <button
                                    key={ex}
                                    onClick={() => speakWord(ex)}
                                    className="bg-white hover:bg-purple-100 text-purple-800 font-bold px-1.5 py-0.5 rounded border border-purple-200 text-[10px] inline-flex items-center gap-0.5"
                                    title={`Listen to "${ex}"`}
                                  >
                                    <span>{ex}</span>
                                    <Volume2 className="w-2.5 h-2.5 text-purple-500" />
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Dispute Toggle */}
                          <div className="flex items-center justify-between gap-1 mt-2.5 pt-2 border-t border-gray-100 text-xs">
                            <span className="text-[11px] text-gray-500 font-semibold">Room verdict:</span>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => handleVoteToggle(activePlayer, cat.id, currentOverride === 'accepted' ? null : 'accepted')}
                                className={`px-2 py-0.5 rounded-lg font-bold text-xs flex items-center gap-1 transition-all ${
                                  currentOverride === 'accepted'
                                    ? 'bg-emerald-600 text-white'
                                    : 'text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200'
                                }`}
                              >
                                <ThumbsUp className="w-3 h-3" />
                                <span>Valid (+10)</span>
                              </button>
                              <button
                                onClick={() => handleVoteToggle(activePlayer, cat.id, currentOverride === 'rejected' ? null : 'rejected')}
                                className={`px-2 py-0.5 rounded-lg font-bold text-xs flex items-center gap-1 transition-all ${
                                  currentOverride === 'rejected'
                                    ? 'bg-red-600 text-white'
                                    : 'text-red-700 bg-red-50 hover:bg-red-100 border border-red-200'
                                }`}
                              >
                                <ThumbsDown className="w-3 h-3" />
                                <span>Dispute (0)</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Stepper Navigation */}
                  <div className="flex items-center justify-between pt-2">
                    <button
                      onClick={() => setActivePartyPlayerIndex(prev => Math.max(0, prev - 1))}
                      disabled={safeIdx <= 0}
                      className="px-3.5 py-1.5 rounded-xl text-xs font-bold border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 flex items-center gap-1 disabled:opacity-40"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      <span>Previous Player</span>
                    </button>
                    <span className="text-xs font-bold text-gray-500">
                      Player {safeIdx + 1} of {playerNames.length}
                    </span>
                    <button
                      onClick={() => setActivePartyPlayerIndex(prev => Math.min(playerNames.length - 1, prev + 1))}
                      disabled={safeIdx >= playerNames.length - 1}
                      className="px-3.5 py-1.5 rounded-xl text-xs font-bold border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 flex items-center gap-1 disabled:opacity-40"
                    >
                      <span>Next Player</span>
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })() : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="border-b border-gray-200 text-gray-500">
                      <th className="py-2 px-2 font-bold uppercase">Player</th>
                      {selectedCategories.map(cat => (
                        <th key={cat.id} className="py-2 px-2 font-bold uppercase">
                          {cat.name}
                        </th>
                      ))}
                      <th className="py-2 px-2 font-bold uppercase text-right">Pts</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 font-medium">
                    {playerNames.map(player => (
                      <tr key={player}>
                        <td className="py-2.5 px-2 font-bold text-gray-900 whitespace-nowrap">
                          {player}
                        </td>
                        {selectedCategories.map(cat => {
                          const valResult = roundValidations[player]?.[cat.id];
                          const catScore = calculatedRoundScores?.[player]?.categoryScores?.[cat.id] ?? (valResult?.isValid ? 10 : 0);
                          const currentOverride = wordOverrides[player]?.[cat.id];
                          return (
                            <td key={cat.id} className="py-2 px-2 align-top">
                              <input
                                type="text"
                                value={roundPlayerAnswers[player]?.[cat.id] || ''}
                                onChange={e => {
                                  const val = e.target.value;
                                  setRoundPlayerAnswers(prev => ({
                                    ...prev,
                                    [player]: {
                                      ...(prev[player] || {}),
                                      [cat.id]: val
                                    }
                                  }));
                                }}
                                placeholder={`${currentLetter}...`}
                                title={
                                  valResult?.isTypo
                                    ? `Spelling mistake: Did you mean "${valResult.correctedWord}"? (-8 pts)`
                                    : undefined
                                }
                                className={`w-28 px-2 py-1.5 rounded-lg border text-xs outline-none ${
                                  valResult
                                    ? currentOverride === 'rejected'
                                      ? 'border-red-400 bg-red-50 text-red-900'
                                      : currentOverride === 'accepted' || valResult.isValid
                                        ? valResult.isTypo && currentOverride !== 'accepted'
                                          ? 'border-amber-400 bg-amber-50 text-amber-900 font-bold'
                                          : 'border-emerald-400 bg-emerald-50 text-emerald-900 font-bold'
                                        : 'border-red-300 bg-red-50 text-red-900'
                                    : 'border-gray-200'
                                }`}
                              />
                              {valResult && (
                                <div className="flex items-center justify-between mt-1 text-[10px]">
                                  <span className={`font-bold ${
                                    catScore > 0 ? 'text-emerald-700' : 'text-red-600'
                                  }`}>
                                    +{catScore} pts
                                  </span>
                                  <div className="flex items-center gap-0.5">
                                    <button
                                      onClick={() => handleVoteToggle(player, cat.id, currentOverride === 'accepted' ? null : 'accepted')}
                                      className={`p-1 rounded ${
                                        currentOverride === 'accepted'
                                          ? 'bg-emerald-600 text-white'
                                          : 'text-gray-400 hover:text-emerald-700 hover:bg-emerald-50'
                                      }`}
                                      title="Vote / Mark Valid (+10 pts)"
                                    >
                                      <ThumbsUp className="w-2.5 h-2.5" />
                                    </button>
                                    <button
                                      onClick={() => handleVoteToggle(player, cat.id, currentOverride === 'rejected' ? null : 'rejected')}
                                      className={`p-1 rounded ${
                                        currentOverride === 'rejected'
                                          ? 'bg-red-600 text-white'
                                          : 'text-gray-400 hover:text-red-700 hover:bg-red-50'
                                      }`}
                                      title="Dispute / Mark Invalid (0 pts)"
                                    >
                                      <ThumbsDown className="w-2.5 h-2.5" />
                                    </button>
                                  </div>
                                </div>
                              )}
                              {valResult?.isTypo && currentOverride !== 'rejected' && (
                                <div className="text-[9px] text-amber-800 font-semibold mt-0.5 truncate max-w-[112px]" title={valResult.correctedWord}>
                                  Typo: {valResult.correctedWord} (-8 pts)
                                </div>
                              )}
                            </td>
                          );
                        })}
                        <td className="py-2 px-2 text-right font-black text-purple-700 whitespace-nowrap">
                          +{calculatedRoundScores?.[player]?.roundScore || 0}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT: Room Players Roster & Leaderboard */}
        <div className="space-y-6">
          {/* Room Leaderboard Card */}
          <div className="bg-white rounded-3xl p-6 shadow-xl border border-gray-100">
            <h3 className="text-base font-extrabold text-gray-900 mb-4 flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-500" />
              Room Leaderboard
            </h3>

            <div className="space-y-2.5">
              {sortedLeaderboard.map((player, idx) => (
                <div
                  key={player}
                  className={`p-3 rounded-2xl flex items-center justify-between border ${
                    idx === 0
                      ? 'bg-amber-50/70 border-amber-300'
                      : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <span
                      className={`w-6 h-6 rounded-full flex items-center justify-center font-bold text-xs ${
                        idx === 0
                          ? 'bg-amber-400 text-amber-900'
                          : 'bg-gray-200 text-gray-700'
                      }`}
                    >
                      {idx + 1}
                    </span>
                    <span className="font-bold text-gray-900 text-sm">{player}</span>
                  </div>
                  <span className="font-black text-base text-purple-900">
                    {playerScores[player] || 0} pts
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Players Roster Management */}
          <div className="bg-white rounded-3xl p-6 shadow-md border border-gray-100">
            <h4 className="text-xs font-bold uppercase text-gray-500 mb-3 tracking-wider flex items-center gap-1.5">
              <Users className="w-4 h-4 text-indigo-500" />
              Players in the Room ({playerNames.length})
            </h4>

            <div className="space-y-2 mb-4">
              {playerNames.map(name => (
                <div
                  key={name}
                  className="flex items-center justify-between p-2.5 bg-gray-50 rounded-xl text-xs font-semibold"
                >
                  <span>{name}</span>
                  {playerNames.length > 1 && (
                    <button
                      onClick={() => handleRemovePlayer(name)}
                      className="text-gray-400 hover:text-red-500 transition-colors p-1"
                      title="Remove player"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>

            <div className="flex gap-2">
              <input
                type="text"
                value={newPlayerInput}
                onChange={e => setNewPlayerInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAddPlayer()}
                placeholder="Add player name..."
                className="flex-1 px-3 py-2 rounded-xl border border-gray-300 text-xs font-medium focus:ring-2 focus:ring-purple-400 focus:outline-none"
              />
              <button
                onClick={handleAddPlayer}
                disabled={!newPlayerInput.trim()}
                className="px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-xl text-xs font-bold transition-all disabled:opacity-50"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Room Settings */}
          <div className="bg-white rounded-3xl p-6 shadow-md border border-gray-100">
            <h4 className="text-xs font-bold uppercase text-gray-500 mb-3 tracking-wider">
              Room Settings
            </h4>
            <div className="space-y-3 text-xs">
              <div>
                <label className="block font-bold text-gray-700 mb-1">Round Timer</label>
                <select
                  value={timerDuration}
                  onChange={e => {
                    const dur = Number(e.target.value);
                    setTimerDuration(dur);
                    setTimeLeft(dur);
                  }}
                  className="w-full bg-white border rounded-xl px-3 py-2 text-xs font-bold"
                >
                  <option value={30}>30 seconds</option>
                  <option value={45}>45 seconds</option>
                  <option value={60}>60 seconds</option>
                  <option value={90}>90 seconds</option>
                  <option value={0}>No Timer (Manual Stop)</option>
                </select>
              </div>

              <button
                onClick={() => {
                  setPlayerScores({});
                  setCurrentRound(1);
                  setUsedLetters([]);
                  startNewRound();
                }}
                className="w-full mt-2 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold rounded-xl text-xs transition-colors"
              >
                Reset Match Scores 🔄
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StopGameParty;
